import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { Button } from '@/components/ui';
import type { TourStep } from '@/help/tours';

/**
 * الجولةُ الإرشادية (27 سبتمبر 2026) — **بلا مكتبةٍ خارجية**: تظليلٌ حول العنصر المشار إليه
 * (`data-tour`) ولوحٌ صغير بجانبه بـ«التالي/السابق/تخطَّ».
 *
 * - الهدفُ أولُ عنصرٍ **ظاهر** يحمل القيمة: بندُ القائمة على الحاسوب وتبويبُ الشريط على الجوال
 *   يحملان القيمةَ نفسَها. وما لا يظهر (قائمةٌ مطويّة) تُعرض خطوتُه في الوسط بلا تظليل — لا تتعطّل.
 * - الموضعُ يُقاس بـ`getBoundingClientRect` ويُرسم بـ`top/left` **إحداثياتٍ هندسية** في `style`
 *   (استثناءٌ مقصود من الخصائص المنطقية، كمؤشّر اللعب المشترك): القياسُ نفسُه فيزيائي.
 * - نافذةٌ حقيقية للوحة المفاتيح وقارئ الشاشة: `role="dialog"`، والتركيزُ ينتقل إلى اللوح مع كل
 *   خطوة ويبقى فيه (Tab يدور على أزراره)، و`Escape` تخطٍّ.
 */
export function Tour({ steps, onDone }: { steps: TourStep[]; onDone: (outcome: 'seen' | 'skipped') => void }) {
  const [i, setI] = useState(0);
  const [box, setBox] = useState<{ spot: DOMRect | null; pos: CSSProperties }>({ spot: null, pos: { visibility: 'hidden' } });
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const step = steps[i];
  const last = i === steps.length - 1;

  const measure = useCallback(() => {
    const el = step.target ? findVisible(step.target) : null;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const p = panel.current;
    const w = Math.min(360, vw - 32);
    const h = p?.offsetHeight ?? 200;
    if (!el) {
      setBox({ spot: null, pos: { top: Math.max(16, (vh - h) / 2), left: (vw - w) / 2, width: w } });
      return;
    }
    const r = el.getBoundingClientRect();
    const below = r.bottom + 12;
    const above = r.top - 12 - h;
    const clampX = (x: number) => Math.min(Math.max(16, x), vw - 16 - w);
    if (below + h <= vh - 16 || above >= 16) {
      setBox({ spot: r, pos: { top: below + h <= vh - 16 ? below : above, left: clampX(r.left + r.width / 2 - w / 2), width: w } });
      return;
    }
    // هدفٌ طويل (القائمةُ الجانبية كلُّها): اللوحُ بجانبه لا فوقه — حيث يتّسع المكان
    const top = Math.min(Math.max(16, r.top), vh - 16 - h);
    const side = r.left - 12 - w >= 16 ? r.left - 12 - w : r.right + 12 + w <= vw - 16 ? r.right + 12 : clampX((vw - w) / 2);
    setBox({ spot: r, pos: { top, left: side, width: w } });
  }, [step.target]);

  // لكل خطوة: أظهر الهدفَ ثم قِس بعد إطارٍ (بعد التمرير)، وأعد القياسَ مع تغيّر الحجم والتمرير
  useEffect(() => {
    const el = step.target ? findVisible(step.target) : null;
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const raf = requestAnimationFrame(() => { measure(); panel.current?.focus(); });
    const on = () => measure();
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', on); window.removeEventListener('scroll', on, true); };
  }, [measure, step.target]);

  // Escape تخطٍّ من أي موضع (التركيزُ قد يكون خارج اللوح لحظةَ فتحه من قائمة) — والدالّةُ من مرجع
  // فلا يُعاد تركيبُ المستمع مع كل تصيير (قاعدةُ `Modal`: تأثيرُ التركيب لا يعتمد على دالّةٍ مُمرَّرة)
  const doneRef = useRef(onDone);
  useEffect(() => { doneRef.current = onDone; });
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); doneRef.current('skipped'); } };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !panel.current) return;
    const focusables = Array.from(panel.current.querySelectorAll<HTMLElement>('button:not([aria-disabled="true"])'));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const end = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { e.preventDefault(); end.focus(); }
    else if (!e.shiftKey && document.activeElement === end) { e.preventDefault(); first.focus(); }
  };

  const pad = 6;
  return (
    <div className="tour" data-testid="tour">
      {box.spot ? (
        <div className="tour__spot" aria-hidden
          style={{ top: box.spot.top - pad, left: box.spot.left - pad, width: box.spot.width + pad * 2, height: box.spot.height + pad * 2 }} />
      ) : (
        <div className="tour__scrim" aria-hidden />
      )}
      <div ref={panel} className="tour__panel" style={box.pos} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKey}>
        <p className="caption tour__count">الخطوة {i + 1} من {steps.length}</p>
        <h2 id={titleId} className="tour__title">{step.title}</h2>
        <p className="tour__text">{step.text}</p>
        <div className="tour__actions">
          <Button variant="primary" size="sm" onClick={() => (last ? onDone('seen') : setI(i + 1))}>{last ? 'إنهاء' : 'التالي'}</Button>
          {i > 0 && <Button variant="secondary" size="sm" onClick={() => setI(i - 1)}>السابق</Button>}
          {!last && <Button variant="ghost" size="sm" className="tour__skip" onClick={() => onDone('skipped')}>تخطَّ الجولة</Button>}
        </div>
      </div>
    </div>
  );
}

/** أولُ عنصرٍ ظاهرٍ أفقياً يحمل القيمة — القائمةُ المطويّة على الجوال خارج الشاشة فلا تُختار */
function findVisible(target: string): HTMLElement | null {
  const els = document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(target)}"]`);
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) continue;
    if (r.right <= 0 || r.left >= window.innerWidth) continue;
    if (typeof el.checkVisibility === 'function' && !el.checkVisibility({ visibilityProperty: true, opacityProperty: true })) continue;
    return el;
  }
  return null;
}
